import { initializeApp } from "firebase/app";
import { getMessaging, getToken, onMessage } from "firebase/messaging";

// Your web app's Firebase configuration
const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID
};

// Initialize Firebase app
const app = initializeApp(firebaseConfig);
const messaging = getMessaging(app);

// Register service worker for background notifications
if ('serviceWorker' in navigator) {
    navigator.serviceWorker.register('/firebase-messaging-sw.js')
        .then((registration) => {
            console.log("Service Worker registered:", registration);
        })
        .catch((error) => {
            console.error("Service Worker registration failed:", error);
        });
}

/**
 * Request notification permission and get FCM token
 * @returns {Promise<string|null>} FCM token or null if permission denied
 */
export const requestNotificationPermission = async () => {
    try {
        // Check if the browser supports notifications
        if (!('Notification' in window)) {
            console.log('This browser does not support notifications');
            return null;
        }

        // Request permission
        const permission = await Notification.requestPermission();
        
        if (permission !== 'granted') {
            console.warn('Notifications blocked. Reset site permissions in browser settings.');
            return null;
        }

        // Get registration token for FCM
        const token = await getToken(messaging, {
            vapidKey: process.env.REACT_APP_FIREBASE_VAPID_KEY
        });

        if (!token) {
            console.error('Failed to get FCM token. Ensure your browser supports notifications.');
            return null;
        }
        
        console.log('FCM Token:', token);
        
        // Store token in localStorage for easy access
        localStorage.setItem('fcm_token', token);
        
        return token;
    } catch (error) {
        console.error('Error getting FCM token:', error);
        return null;
    }
};

/**
 * Listen for foreground messages
 * @returns {Promise} Promise that resolves with the notification payload
 */
export const onMessageListener = () => {
    return new Promise((resolve) => {
        onMessage(messaging, (payload) => {
            console.log('Foreground notification received:', payload);
            
            // Display a browser notification if the app is in foreground
            if (Notification.permission === 'granted') {
                const { title, body, icon } = payload.notification || {};
                
                // Only create a notification if we have a title
                if (title) {
                    const notification = new Notification(title, {
                        body: body || '',
                        icon: icon || '/favicon.ico'
                    });
                    
                    // Handle notification click
                    notification.onclick = () => {
                        window.focus();
                        notification.close();
                    };
                }
            }
            
            resolve(payload);
        });
    });
};

/**
 * Initialize notifications for a specific user
 * @param {string} userId - The user's ID
 */
export const initializeUserNotifications = async (userId) => {
    try {
        // Get or request FCM token
        let token = localStorage.getItem('fcm_token');
        
        if (!token) {
            token = await requestNotificationPermission();
        }
        
        if (token) {
            // Subscribe to user-specific topic if needed
            console.log(`User ${userId} initialized for notifications with token ${token}`);
            
            // You can add server communication here to associate the token with the user
            // Example: await axios.post('/api/notifications/register', { userId, token });
            
            return token;
        }
    } catch (error) {
        console.error('Error initializing notifications:', error);
        return null;
    }
};

// Export the Firebase app instance
export default app;